/**
 * URL utility functions
 */

/**
 * Sanitizes sensitive OAuth parameters from URLs for secure logging
 * Replaces sensitive parameter values with '[REDACTED]'
 * @param url - The URL to sanitize
 * @returns The sanitized URL, or original URL if parsing fails
 */
export const sanitizeUrl = (url: string): string => {
  if (!url) return url;

  try {
    const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:\/\//i.test(url);
    const isRelativeUrl = url.startsWith('/') && !url.startsWith('//');

    if (!isAbsoluteUrl && !isRelativeUrl) {
      return url;
    }

    const urlObj = isAbsoluteUrl
      ? new URL(url)
      : new URL(url, 'https://sanitizer.avoma.local');
    const sensitiveParams = [
      'code',
      'access_token',
      'refresh_token',
      'id_token',
      'token',
      'api_key',
      'api-key',
      'session',
      'password',
      'secret',
      'jwt',
      'authorization',
      'auth'
    ];
    const isOauthCallbackPath =
      /^\/oauth(?:\/|$)/.test(urlObj.pathname) ||
      /^\/okta\/oidc(?:\/|$)/.test(urlObj.pathname);
    if (isOauthCallbackPath) {
      sensitiveParams.push(
        'error',
        'error_description',
        'error_uri',
        'state',
        'session_state'
      );
    }
    sensitiveParams.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    });

    if (isAbsoluteUrl) {
      return urlObj.toString();
    }

    if (isRelativeUrl) {
      return `${urlObj.pathname}${urlObj.search}${urlObj.hash}`;
    }

    return url;
  } catch (error) {
    // Return original URL if parsing fails - better to log something than nothing
    return url;
  }
};
